import type { ChatUpload } from './useAgentChat.ts';
import './UploadCard.css';

interface UploadCardProps {
  /** The transcript item this card renders. */
  item: ChatUpload;
  /** For the copy that tells the user who to ask. */
  agentName: string;
  /** Abort the transfer. Absent when the console cannot start uploads at all,
   *  in which case no upload card can exist and Cancel is simply not offered —
   *  never a no-op stand-in that looks clickable and does nothing. */
  onCancel?: () => void;
}

/**
 * An uploaded file in the chat stream: progress while it transfers, then the
 * in-agent path and the expiry warning, or the failure reason.
 *
 * The expiry line is not decoration. These files are stored `tier=ephemeral`
 * and the bucket deletes them after 7 days; a user who is not told will lose
 * work silently. The wording says "save a copy" rather than "move it" because
 * there is no doc_move or doc_copy tool — an agent keeps a file by reading it
 * and writing it somewhere persistent.
 */
export default function UploadCard({ item, agentName, onCancel }: UploadCardProps) {
  const { name, path, progress, status, message } = item;
  const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100);
  return (
    <div className={`om-upload om-upload--${status}`}>
      <div className="om-upload__head">
        <PaperclipIcon />
        <span className="om-upload__name">{name}</span>
        {status === 'uploading' && onCancel ? (
          <button type="button" className="om-upload__cancel" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>

      {status === 'uploading' ? (
        <div
          className="om-upload__bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label={`Uploading ${name}`}
        >
          <div className="om-upload__bar-fill" style={{ width: `${pct}%` }} />
        </div>
      ) : null}

      {status === 'done' ? (
        <p className="om-upload__body">
          Uploaded and available to {agentName} at <code className="om-upload__path">{path}</code>.{' '}
          <strong>Expires in 7 days</strong> — ask {agentName} to save a copy if you want to keep
          it.
        </p>
      ) : null}

      {status === 'error' ? <p className="om-upload__error">{message}</p> : null}
    </div>
  );
}

/** Paperclip glyph, matching the composer button that started the upload. */
function PaperclipIcon() {
  return (
    <svg
      className="om-upload__icon"
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M21 11.5l-8.6 8.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.4 3.4 0 0 1 4.8 4.8l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
